import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Eye, EyeSlash, X, ArrowSquareOut } from "@phosphor-icons/react";

const BLANK = { title: "", category: "Guide", slug: "", excerpt: "", body: "", published: false };

export default function AdminGuides() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {..fields, id?}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/guides").then(({ data }) => setGuides(data.guides || [])).catch(() => toast.error("Could not load guides")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/guides/${editing.id}`, editing);
      else await api.post("/admin/guides", editing);
      toast.success(editing.id ? "Guide updated" : "Guide created");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (g) => {
    try {
      const { data } = await api.patch(`/admin/guides/${g.id}/publish`);
      toast.success(data.published ? "Guide is now live" : "Guide hidden");
      load();
    } catch { toast.error("Could not update"); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/guides/${g.id}`); toast.success("Guide deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-black text-navy text-2xl tracking-tight">Month-End Guides</h1>
          <p className="text-navy/70 text-sm mt-1">Write, publish or hide blog guides. Published guides appear on /guides.</p>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} data-testid="new-guide-btn"
          className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-navy/90 transition-colors">
          <Plus size={16} weight="bold" /> New guide
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-navy/65" data-testid="admin-guides-loading">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden" data-testid="admin-guides-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-navy/70 uppercase tracking-wide">Title</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-navy/70 uppercase tracking-wide">Category</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-navy/70 uppercase tracking-wide">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {guides.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-10 text-center text-navy/65">No guides yet. Create your first one.</td></tr>
              )}
              {guides.map((g) => (
                <tr key={g.id} data-testid={`admin-guide-${g.slug}`} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-navy">{g.title}</div>
                    <div className="text-xs text-navy/65">/{g.slug} · {g.read_minutes} min</div>
                  </td>
                  <td className="px-5 py-3.5 text-navy/70">{g.category}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${g.published ? "bg-positive/10 text-positive" : "bg-navy/5 text-navy/70"}`}>
                      {g.published ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      {g.published && (
                        <a href={`/guides/${g.slug}`} target="_blank" rel="noreferrer" title="View" data-testid={`view-${g.slug}`}
                          className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/70 hover:text-orange hover:border-orange/40 transition-colors"><ArrowSquareOut size={15} /></a>
                      )}
                      <button onClick={() => togglePublish(g)} data-testid={`publish-${g.slug}`} title={g.published ? "Hide" : "Publish"}
                        className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/70 hover:text-navy transition-colors">
                        {g.published ? <EyeSlash size={15} /> : <Eye size={15} />}
                      </button>
                      <button onClick={() => setEditing({ ...g })} data-testid={`edit-${g.slug}`} title="Edit"
                        className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/70 hover:text-navy transition-colors"><PencilSimple size={15} /></button>
                      <button onClick={() => del(g)} data-testid={`delete-${g.slug}`} title="Delete"
                        className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 text-navy/65 hover:text-red-600 hover:border-red-300 transition-colors"><Trash size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="guide-editor" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="font-heading font-bold text-navy text-lg">{editing.id ? "Edit guide" : "New guide"}</h2>
              <button onClick={() => setEditing(null)} data-testid="editor-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Title">
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="editor-title" className={inp} placeholder="How to account for prepayments in Xero" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category"><input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="editor-category" className={inp} /></Field>
                <Field label="Slug (optional)"><input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="editor-slug" className={inp} placeholder="auto from title" /></Field>
              </div>
              <Field label="Excerpt (shown on cards & search)">
                <textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} data-testid="editor-excerpt" rows={2} className={inp} />
              </Field>
              <Field label="Body (Markdown)">
                <textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="editor-body" rows={14} className={`${inp} font-mono text-xs leading-relaxed`} placeholder="## Heading&#10;&#10;Paragraph text with **bold**, lists and | tables |." />
              </Field>
              <label className="flex items-center gap-2 text-sm text-navy cursor-pointer">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="editor-published" className="accent-orange" />
                Published (visible on the site)
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="editor-save" className="px-5 py-2 text-sm font-semibold rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const inp = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-colors";
const Field = ({ label, children }) => (
  <div><div className="text-xs font-semibold text-navy/70 uppercase tracking-wide mb-1.5">{label}</div>{children}</div>
);
